import { ReactNode } from 'react'
import { ArrowRight, type LucideIcon } from 'lucide-react'
import { Link } from 'react-router-dom'

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle
} from '@/components/ui/card'
import { cn } from '@/lib/utils'
import { useIntlLocale } from '@/i18n/languages'

export function DashboardCard({
  icon: Icon,
  title,
  action,
  children,
  className
}: {
  icon: LucideIcon
  title: ReactNode
  action?: ReactNode
  children: ReactNode
  className?: string
}) {
  return (
    <Card className={cn('flex h-full flex-col', className)}>
      <CardHeader className="flex-row items-center justify-between space-y-0 pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Icon className="h-5 w-5 text-primary" />
          {title}
        </CardTitle>
        {action}
      </CardHeader>
      <CardContent className="flex-1">{children}</CardContent>
    </Card>
  )
}

type Tone = 'teal' | 'amber' | 'gray' | 'red'

const toneStyles: Record<Tone, string> = {
  teal: 'bg-primary/10 text-primary',
  amber: 'bg-peach/25 text-warning',
  gray: 'bg-ink/[0.07] text-ink/60',
  red: 'bg-destructive/10 text-destructive'
}

export function Badge({
  tone = 'gray',
  children
}: {
  tone?: Tone
  children: ReactNode
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold',
        toneStyles[tone]
      )}
    >
      {children}
    </span>
  )
}

export function CardLink({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link
      to={to}
      className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline"
    >
      {children} <ArrowRight className="h-4 w-4" />
    </Link>
  )
}

export function Row({
  label,
  children
}: {
  label: ReactNode
  children: ReactNode
}) {
  return (
    <div className="flex items-center justify-between gap-3 py-1.5">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-sm font-medium text-ink">{children}</span>
    </div>
  )
}

export function CardSkeleton() {
  return (
    <div className="space-y-3">
      <div className="h-4 w-2/3 animate-pulse rounded bg-ink/[0.06]" />
      <div className="h-4 w-1/2 animate-pulse rounded bg-ink/[0.06]" />
      <div className="h-4 w-3/5 animate-pulse rounded bg-ink/[0.06]" />
    </div>
  )
}

export function EmptyState({
  text,
  action
}: {
  text: ReactNode
  action?: ReactNode
}) {
  return (
    <div className="flex h-full flex-col items-start justify-center gap-3 py-2">
      <p className="text-sm text-muted-foreground">{text}</p>
      {action}
    </div>
  )
}

export function useFormatDate() {
    const locale = useIntlLocale()
  return (value?: string | null) =>
    value
      ? new Date(value).toLocaleDateString(locale, {
          day: 'numeric',
          month: 'short',
          year: 'numeric'
        })
      : '—'
}
